<template>
  <div>
    <DataTable
      ref="DataTable"
      :data-model="'products/archived'"
      :display-name-plural="$t('entities.ProductArchive.Plural')"
      :display-name-singular="$t('entities.ProductArchive.Singular')"
      :permission-name="'Products'"
      :allow-delete="false"
      :allow-create="false"
      :allow-edit="false"
      :exclude-fields="['id', 'isInvalid', 'isInProgress']"
      :action-disabled-callback="actionDisabled"
      @input="onDataTableInput">
      <template #cell(status)="row">
        {{ $t('products.Status.'+row.item.status) }}
      </template>
      <template #cell(color)="row">
        <div :style="'min-height:15px;background-color:'+(colors.filter(x => x.id == row.item.color)[0].value)"
             v-if="row.item.color !== 0" />
      </template>
      <template #cell(approvedByLaboratory)="row">
        <b-form-checkbox v-model="row.item.approvedByLaboratory"
                         @change="productToApproveItem = row.item;"
                         :disabled="true" />
      </template>
      <template #cell(pairedPhenotypesCount)="row">
        <div :class="row.item.isInvalid ? 'invalid-product-count': ''">
          {{ row.item.pairedPhenotypesCount }}
        </div>
      </template>
      <template #extraActions="row">
        <el-tooltip
          :content="$t('common.DownloadPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtn mx-1"
                    @click="onButtonClick(row, download)"
                    :disabled="actionDisabled(row)">
            <font-awesome-icon class="fa-xs"
                               icon="download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.DownloadDoctorsIntroPDF')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtnDoctor mx-1"
                    @click="onButtonClick(row, downloadDoctorsIntro)"
                    :disabled="actionDisabled(row)">
            <font-awesome-icon class="fa-xs"
                               icon="file-download" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.DownloadProcessProductReport')"
          :enterable="false"
          placement="top">
          <b-button class="downloadBtnProcessProductReport mx-1"
                    @click="onButtonClick(row, downloadProcessProductReport)"
                    :disabled="actionDisabled(row)">
            <font-awesome-icon class="fa-md"
                               icon="tasks" />
          </b-button>
        </el-tooltip>
        <el-tooltip
          :content="$t('common.MoveFromArchive')"
          :enterable="false"
          placement="top">
          <b-button class="moveProductFromArchiveBtn mx-1"
                    @click="toMoveFromArchive = row.item; $refs.moveFromArchiveModal.show();"
                    :disabled="actionDisabled(row)">
            <font-awesome-icon class="fa-md"
                               icon="box-open" />
          </b-button>
        </el-tooltip>
      </template>
    </DataTable>
    <b-modal id="modal-center"
             ref="downloadModal"
             centered
             :ok-disabled="true"
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.Downloading') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t(downloadModalMessageKey) }}
      </p>
      <div id="nprogressDownload"
           style="height:25px" />
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="danger"
          @click="cancelDownload(); $refs.downloadModal.hide()">
          {{ $t('common.Cancel') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal id="modal-center"
             ref="moveFromArchiveModal"
             centered
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.MoveFromArchive') }}
          </h3>
        </div>
      </template>
      <p class="my-4 text-center">
        {{ $t('common.RealyMoveFromArchiveQuestion') }}
      </p>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          @click="toMoveFromArchive = null; $refs.moveFromArchiveModal.hide();">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          @click="restoreProductFromArchive(toMoveFromArchive);">
          {{ $t('common.Yes') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import DataTable from '@components/admin/DataTable';
import NProgress from 'nprogress/nprogress';
import DoctorColors from '@utils/doctorColors';
import { downloadBlob } from '@utils/downloadBlob';
import { updatePairedPhenotypesCountMixin } from '@components/mixins/updatePairedPhenotypesCount';

export default {
  components: {
    DataTable
  },
  mixins: [
    updatePairedPhenotypesCountMixin
  ],
  data() {
    return {
      source: null,
      colors: DoctorColors,
      doctorToAssign: 0,
      productToAssign: 0,
      productToApproveItem: null,
      isMainDoctor: false,
      downloadModalMessageKey: 'common.GeneratingPDF',
      toMoveFromArchive: null
    };
  },
  computed: {
    role() {
      return this.$store.state['vue-auth']?.user?.role || '';
    },
    isLaborant() {
      return this.role === 'Laboratory Scientist';
    },
    isAdmin() {
      return this.role === 'Administrator';
    }
  },
  mounted() {
    this.source = this.axios.CancelToken.source();
    this.axios.get('/admin/auth/ismaindoctor')
      .then((res) => {
        this.isMainDoctor = res.data.result;
      });
  },
  methods: {
    onButtonClick(row, callback) {
      callback(row.item);
    },
    actionDisabled(row) {
      return row.item.isInProgress;
    },
    cancelDownload() {
      this.source.cancel();
      this.source = this.axios.CancelToken.source();
    },
    download(item) {
      this.showDownloadModal('common.GeneratingPDF');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getpdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Vysledek_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    downloadDoctorsIntro(item) {
      this.showDownloadModal('common.GeneratingPDF');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getdoctorsintropdf', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Lekarska_Zprava_${today.toLocaleDateString('en-US')} ${item.customer}.pdf`,
          'application/pdf'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    downloadProcessProductReport(item) {
      this.showDownloadModal('common.GeneratingProcessProductReport');
      NProgress.configure({ parent: '#nprogressDownload' });
      NProgress.start();
      this.axios.get('admin/products/getprocessproductreport', {
        params: {
          ProductId: item.id
        },
        responseType: 'arraybuffer',
        cancelToken: this.source.token
      }).then((response) => {
        const today = new Date();
        downloadBlob(
          response.data,
          `GenScan_Report_Vytvoreni_Vysledku_${today.toLocaleDateString('en-US')}_${item.customer}.txt`,
          'application/text'
        );
        this.hideDownloadModal();
      })
        .catch((thrown) => {
          if (this.axios.isCancel(thrown)) {
            this.$root.$bvToast.toast(this.$t('common.DownloadCancel'), {
              title: this.$t('common.Cancelled'),
              variant: 'danger',
              toaster: 'b-toaster-top-right'
            });
            this.hideDownloadModal();
          }
        })
        .finally(() => {
          NProgress.configure({ parent: 'body' });
          NProgress.done();
        });
    },
    restoreProductFromArchive(item) {
      this.axios.post('admin/products/moveToDoctorSection', {
        ProductId: item.id
      }).then(() => {
        this.$root.$bvToast.toast(this.$t('Archive.RemoveFrom.SuccessToast.Body'), {
          title: this.$t('Archive.RemoveFrom.SuccessToast.Title'),
          variant: 'success',
          toaster: 'b-toaster-top-right'
        });
        this.$refs.DataTable.removeRowById(item.id);
      }).catch(() => {
        this.$root.$bvToast.toast(this.$t('Archive.RemoveFrom.FailToast.Body'), {
          title: this.$t('Archive.RemoveFrom.FailToast.Title'),
          variant: 'danger',
          toaster: 'b-toaster-top-right'
        });
      }).finally(() => {
        this.toMoveFromArchive = null;
        this.$refs.moveFromArchiveModal.hide();
        NProgress.configure({ parent: 'body' });
        NProgress.done();
      });
    },
    showDownloadModal(messageKey) {
      this.downloadModalMessageKey = messageKey;
      this.$refs.downloadModal.show();
    },
    hideDownloadModal() {
      this.$refs.downloadModal.hide();
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@colors';
.downloadBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #c95328;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.downloadBtnDoctor {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: #3e67ec;
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.downloadBtnProcessProductReport {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: rgb(93, 129, 93);
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.moveProductFromArchiveBtn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  background-color: rgb(180, 154, 35);
  color: $text-on-secondary;
  border: none;
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
    background-color: darken($secondary-variant-color, 10%);
    color: $text-on-secondary;
  }
}

.invalid-product-count {
  color: crimson;
}
</style>
